import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDashboardWidgetSetting } from 'hooks/useDashboardState'
import { useDashboardWidgetData } from 'hooks/useWidgetState'
import { SocialWebChannelsWidget } from 'schemas/SocialWebChannelsWidgetSchema'
import { InsightCount } from 'schemas/action/InsightCountSchema'
import { GroupMetricStrings } from 'enums/GroupMetric'
import GroupTable from 'components/visualization/Table/GroupTable'
import ErrorMessage from 'components/share/ErrorMessage'
import TableSortOption from 'enums/TableSortOption'
import { useDisplayOptions } from './hooks'
import { generateStaticColdefs, dynamicColdefs } from './colDefs'
import {
  retrieveInsightDataSet,
  convertToTableRowData,
  generateMetricsAggregations,
  generateGroupedRowDataByNetworkType,
  overwriteGroupMetricResults,
  filterOutZeroBrandPosts,
  getGroupTitles
} from './utils'

interface ChannelsProps {
  widgetId: number
}

const Channels = ({ widgetId }: ChannelsProps) => {
  const { t } = useTranslation()
  const { groupMetrics: selectedGroupMetrics } = useDashboardWidgetSetting<SocialWebChannelsWidget>(
    widgetId
  )
  const widgetData = useDashboardWidgetData<InsightCount>(widgetId)

  const {
    isByNetworkType,
    hasPercentShare,
    hasAggregateRows,
    shouldHideChannelsWithZeroBrandPosts
  } = useDisplayOptions({ widgetId })

  const coldefs = [
    ...generateStaticColdefs(isByNetworkType),
    ...dynamicColdefs.filter((colDef) => selectedGroupMetrics!.includes(colDef.field))
  ]

  const insightDataSet = retrieveInsightDataSet(widgetData)
  const tableRowData = useMemo(() => {
    const filteredInsightDataSet = shouldHideChannelsWithZeroBrandPosts
      ? filterOutZeroBrandPosts(insightDataSet)
      : insightDataSet
    const processedInsightDataSet = overwriteGroupMetricResults(filteredInsightDataSet)
    return convertToTableRowData(processedInsightDataSet)
  }, [shouldHideChannelsWithZeroBrandPosts, insightDataSet])

  const groupedRowData = useMemo(
    () => (isByNetworkType ? generateGroupedRowDataByNetworkType(tableRowData) : [tableRowData]),
    [isByNetworkType, tableRowData]
  )

  const { metricTotals: grandTotals, metricAverages: grandAverages } = useMemo(
    () => generateMetricsAggregations(tableRowData, selectedGroupMetrics as GroupMetricStrings[]),
    [tableRowData, selectedGroupMetrics]
  )

  const groupedContext = useMemo(
    () =>
      groupedRowData.map((currentRowData) => ({
        hasPercentShare,
        ...generateMetricsAggregations(
          currentRowData,
          selectedGroupMetrics as GroupMetricStrings[]
        ),
        grandTotals,
        grandAverages
      })),
    [grandAverages, grandTotals, groupedRowData, hasPercentShare, selectedGroupMetrics]
  )

  const footerContext = {
    hasPercentShare,
    metricTotals: grandTotals,
    metricAverages: grandAverages,
    grandTotals,
    grandAverages
  }

  const groupTitles = isByNetworkType ? getGroupTitles(tableRowData) : []

  return tableRowData.length === 0 ? (
    <ErrorMessage>{t('noDataReturnedForTheCurrentSettings')}</ErrorMessage>
  ) : (
    <GroupTable
      groupedRowData={groupedRowData}
      groupedContext={groupedContext}
      colDefs={coldefs}
      footerContext={footerContext}
      isDisplayFoot={hasAggregateRows || false}
      groupTitles={groupTitles}
      configs={{
        colSortingConfigs: [{ field: 'channelName', order: TableSortOption.ASCEND }],
        keyField: 'channelId'
      }}
    />
  )
}

export default Channels
